/** `YYYY-MM-DD` in UTC — the identity of a day's challenge everywhere. */
export function utcDateString(now) {
    return now.toISOString().slice(0, 10);
}
/** Today's challenge date, from the injected clock (never `Date.now()` directly). */
export function dailyDate(clock) {
    return utcDateString(new Date(clock.now()));
}
/**
 * A small integer hash of the date string (FNV-1a, 32-bit). Only spread
 * matters — consecutive dates must land far apart in seed space, which FNV
 * does and `year*10000+month*100+day` would not (the generator salts seeds
 * linearly, so near-linear inputs risk correlated boards).
 */
export function dailyHash(date) {
    let h = 0x811c9dc5;
    for (let i = 0; i < date.length; i += 1) {
        h ^= date.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/** The generator seed for a date — the hash, whole. */
export function dailySeed(date) {
    return dailyHash(date);
}
/**
 * The effective level a date's board is generated at: 25–55, hash-picked.
 *
 * 25 is the first powered-gate level (Module 63's `poweredGatesUnlockLevel`
 * region), 55 is deep Intermediate — hard days are genuinely hard without
 * demanding Expert's full shape/colour vocabulary from a player who may never
 * have entered that track. A different date walks a different point of the
 * band, so the daily has texture; within one date it is one number for
 * everyone.
 */
export const DAILY_LEVEL_MIN = 25;
export const DAILY_LEVEL_MAX = 55;
export function dailyEffectiveLevel(date) {
    const span = DAILY_LEVEL_MAX - DAILY_LEVEL_MIN + 1;
    // A second, decorrelated draw: reuse the hash at a different rotation so the
    // level is not a function of the seed's low bits (which the generator also
    // consumes).
    const h = dailyHash(`level:${date}`);
    return DAILY_LEVEL_MIN + (h % span);
}
/** Yesterday's date string, for the streak rule. */
export function utcYesterday(date) {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return utcDateString(d);
}
/**
 * The streak after clearing on `today`, given the last cleared date (or
 * undefined). Cleared yesterday → the run continues; a gap, a same-day
 * replay (impossible via the UI, but the maths should not mint streaks), or
 * no history → the run restarts at 1.
 */
export function streakAfterClear(today, lastClearDate, previousStreak) {
    if (lastClearDate === utcYesterday(today))
        return Math.max(1, previousStreak) + 1;
    if (lastClearDate === today)
        return Math.max(1, previousStreak);
    return 1;
}
